import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// v14.17.16 — `withCredentials: true` is REQUIRED so the shared
// `api` client sends the `hub_session` cookie on every request.
// Without it, the browser silently drops the cookie for cross-origin
// requests (frontend on `www.theprophets.io` calling `theprophets.io`,
// or preview vs production hosts) and every protected `/hub/*`
// endpoint returns 401. That was the actual root cause of the
// "half-authenticated" state where the Profile header still showed
// BigJ (loaded via the `hub` client which had the flag) while the
// Daily Reward widgets and the Prophets Friend invitation card
// (both using this shared `api` client) rendered blank or
// "Not authenticated". Every Hub axios client must set this flag.
export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

const TOKEN_KEY = "cityrun_admin_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ── v14.17.16 · centralized Hub-session revalidation ────────────
// When a Hub-protected `/hub/*` request comes back 401 despite the
// UI still showing a logged-in user, revalidate the session via
// `GET /hub/me` ONCE (not per widget). If `/hub/me` also 401s the
// session is truly gone: dispatch a single `hub:session-lost`
// event so pages can clear their stale UI and present a centralized
// CONNECT DISCORD recovery state without every widget rendering
// its own "Not authenticated" banner.
//
// Guardrails:
//   * Only revalidates `/hub/*` responses (admin `/family-race/*`
//     still uses its own token-based flow).
//   * Skips revalidation for `/hub/me` itself so the interceptor
//     never recurses.
//   * A single revalidation is in-flight at a time and the result
//     is shared, so bursty parallel widget mounts (Profile hydrates
//     ~5 widgets in the same paint) hit the network once.
//   * Transient Discord role-refresh backoff / 429 / 5xx on other
//     endpoints is NOT treated as a session-loss signal.
let _revalidatePromise = null;
async function _revalidateHubSession() {
  if (_revalidatePromise) return _revalidatePromise;
  _revalidatePromise = (async () => {
    try {
      const r = await api.get("/hub/me", {
        // Mark so the interceptor doesn't recurse.
        headers: { "X-Hub-Revalidate": "1" },
      });
      return !!r?.data?.authenticated;
    } catch {
      return false;
    } finally {
      // Small debounce so a burst of 401s in the same paint cycle
      // all share this revalidation result.
      setTimeout(() => { _revalidatePromise = null; }, 500);
    }
  })();
  return _revalidatePromise;
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const status = err?.response?.status;
    const url    = String(err?.config?.url || "");
    const isHub  = url.startsWith("/hub/") || url.includes("/hub/");
    const isSelf = url.includes("/hub/me")
                     || err?.config?.headers?.["X-Hub-Revalidate"] === "1";

    if (status === 401 && isHub && !isSelf) {
      const stillAuthed = await _revalidateHubSession();
      if (!stillAuthed) {
        try {
          window.dispatchEvent(new CustomEvent("hub:session-lost"));
        } catch { /* SSR / very old browsers — noop */ }
      }
    }

    if (status === 401) {
      // Admin token invalidation (unchanged behaviour).
      const path = window.location.pathname;
      if (path.startsWith("/family-race/admin") && path !== "/family-race/admin/login") {
        setToken(null);
      }
    }
    return Promise.reject(err);
  },
);

export function apiErrorMessage(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" · ");
  }
  return typeof detail === "object" && detail.msg ? detail.msg : fallback;
}
